import axios, { AxiosError, type AxiosInstance } from 'axios'

/**
 * 서버와 이야기하는 통로. 화면은 이것만 씁니다.
 *
 * <p><b>같은 API 를 씁니다.</b> 도메인만 다릅니다 — 뒤에 있는 자료가 같아야
 * 사무실에서 잡아 준 예약을 손님이 바로 봅니다.</p>
 *
 * <p>다만 <b>손님이 부를 수 있는 통로는 아주 적습니다.</b> 회사 화면이 쓰는
 * 통로를 그대로 열어 주면 남의 예약까지 보이므로, 서버가 손님 몫으로 따로
 * 열어 둔 것만 부릅니다.</p>
 */

/**
 * 토큰을 담는 자리.
 *
 * <p><b>다른 화면과 이름을 달리했습니다.</b> 도메인이 다르면 저장 자리도 원래
 * 갈리지만, 개발 중에는 같은 <c>localhost</c> 에 포트만 달라 <b>한 자리를 같이 쓰게</b>
 * 됩니다. 그러면 한쪽에서 로그아웃할 때 다른 쪽도 튕깁니다.</p>
 */
const TOKEN_KEY = 'rcms.customer.token'

/**
 * <b>탭을 닫아도 남깁니다</b>(<c>localStorage</c>).
 *
 * <p><b>손님 본인의 손전화</b>입니다. 예약을 확인하러 며칠에 걸쳐 몇 번
 * 들어오는데, 그때마다 예약번호를 다시 치게 하면 대개 그냥 전화를 겁니다.</p>
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

/** 서버가 거절한 이유. 화면이 무엇을 보여 줄지 고르는 데 씁니다. */
export type ApiFailure =
  | { kind: 'unauthenticated' }
  | { kind: 'forbidden'; message: string }
  | { kind: 'notFound' }
  | { kind: 'offline' }
  | { kind: 'server'; message: string }

export class ApiError extends Error {
  readonly failure: ApiFailure

  constructor(failure: ApiFailure) {
    super('message' in failure ? failure.message : failure.kind)
    this.failure = failure
    this.name = 'ApiError'
  }
}

/** 토큰이 죽었을 때 화면을 로그인으로 보내는 손잡이. `main.ts` 가 채웁니다. */
let onUnauthenticated: (() => void) | null = null

export function setUnauthenticatedHandler(fn: () => void): void {
  onUnauthenticated = fn
}

/** 저장이 끝났을 때 알리는 손잡이. `main.ts` 가 채웁니다. */
let notifySaved: ((message: string) => void) | null = null

export function setSavedNotifier(fn: (message: string) => void): void {
  notifySaved = fn
}

export const http: AxiosInstance = axios.create({
  baseURL: '/api',
  // 손전화는 전파가 오락가락합니다. 짧게 끊고 "안 됐다" 고 알려 주는 편이,
  // 눌러 놓고 기다리게 하는 것보다 낫습니다.
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
})

http.interceptors.request.use((config) => {
  const token = getToken()
  if (token) config.headers.Authorization = `Bearer ${token}`

  // 파일을 올릴 때는 Content-Type 을 지워야 합니다. 그대로 두면 서버가 415 로
  // 되돌려보냅니다. 지우면 브라우저가 multipart 경계값까지 채웁니다.
  if (config.data instanceof FormData) delete config.headers['Content-Type']

  return config
})

/**
 * 이 요청이 끝났을 때 <b>무슨 말을 띄울지</b>. 띄우지 않을 것이면 <c>null</c>.
 *
 * <p><b>방식만 보고 정하면 안 됩니다.</b> 2026-09-03 담당자가
 * <b>"로그인을 하는데 저장되었다는 메세지가 나오고 있어"</b> 라고 알려 주었습니다 —
 * 로그인도 <code>POST</code> 라서 "저장했습니다" 가 떴습니다. 로그인은 저장이 아닙니다.</p>
 *
 * <p>그래서 <b>주소도 함께</b> 봅니다. 판단을 이름 붙여 꺼내 둔 이유는
 * 통로 안에 묻어 두면 시험으로 확인할 수 없기 때문입니다.</p>
 */
/**
 * 이 요청이 <b>본인 확인 시도</b>인가.
 *
 * <p>401 은 보통 "열쇠가 죽었다" 는 뜻이라 열쇠를 지우고 처음 화면으로 보냅니다.
 * 그런데 <b>본인 확인 자체가 틀렸을 때도 401</b> 입니다. 그것까지 같이 다루면
 * 손님이 번호를 잘못 넣었을 뿐인데 <b>"확인 시간이 지났습니다"</b> 라는 엉뚱한 말을
 * 봅니다 — 2026-09-03 에 실제로 그랬습니다.</p>
 *
 * <p>직원 앱에서 <b>"로그인을 하는데 저장되었다는 메세지가 나오고 있어"</b> 로 겪은 것과
 * 같은 갈래의 실수입니다. 방식(POST)이나 상태(401)만 보고 판단하면 안 되고
 * <b>주소도 함께</b> 봐야 합니다. 시험할 수 있게 이름 붙여 꺼내 둡니다.</p>
 */
export function isSignInAttempt(url: string | undefined): boolean {
  return (url ?? '').includes('/customer/verify')
}

export function toastFor(method: string | undefined, url: string | undefined): string | null {
  // 로그인·내정보 확인 같은 <b>드나드는 일</b>은 저장이 아닙니다.
  if ((url ?? '').includes('/auth/')) return null

  /* <b>대화는 저장이 아닙니다.</b> 채팅은 한 줄 칠 때마다 POST 라서, 말을 보낼 때마다
     "저장했습니다" 가 떴습니다(2026-09-29 담당자 지적). 손님은 글을 보낸 것이지
     무엇을 저장한 것이 아닙니다 — 보낸 말이 화면에 붙는 것이 이미 답입니다.
     로그인이 POST 라서 "저장했습니다" 가 뜨던 것과 같은 갈래입니다. */
  if ((url ?? '').includes('/chat')) return null

  switch ((method ?? '').toLowerCase()) {
    case 'post': case 'put': case 'patch': return '저장했습니다.'
    case 'delete': return '취소했습니다.'
    default: return null
  }
}

http.interceptors.response.use(
  (response) => {
    const config = response.config as typeof response.config & { quiet?: boolean }
    if (!config.quiet) {
      const message = toastFor(config.method, config.url)
      if (message) notifySaved?.(message)
    }
    return response
  },
  (error: AxiosError<{ message?: string }>) => {
    // 손전화라 <b>전파가 끊긴 것</b>을 따로 알려 줍니다.
    // "처리 중 문제가 생겼습니다" 로 뭉뚱그리면 손님은 회사 잘못으로 읽습니다.
    if (error.response === undefined) {
      return Promise.reject(new ApiError({ kind: 'offline' }))
    }

    const status = error.response.status
    const serverMessage = error.response.data?.message

    if (status === 401) {
      // 본인 확인이 틀린 것은 <b>열쇠가 죽은 것이 아닙니다.</b> 서버가 적어 준 말을
      // 그대로 올려 보내고, 화면을 옮기지도 열쇠를 지우지도 않습니다.
      if (isSignInAttempt(error.config?.url)) {
        return Promise.reject(new ApiError({
          kind: 'server',
          message: serverMessage ?? '예약번호나 전화번호가 맞지 않습니다.',
        }))
      }

      clearToken()
      onUnauthenticated?.()
      return Promise.reject(new ApiError({ kind: 'unauthenticated' }))
    }

    if (status === 403) {
      return Promise.reject(new ApiError({
        kind: 'forbidden',
        message: serverMessage ?? '이 일을 할 권한이 없습니다.',
      }))
    }

    if (status === 404) return Promise.reject(new ApiError({ kind: 'notFound' }))

    // 502·503·504 는 <b>서버에 못 닿았다</b>는 뜻입니다 — 앞단 웹서버가 뒤를
    // 못 찾았거나(502), 내려가 있거나(503), 대답이 늦은(504) 것입니다.
    //
    // 이것을 여느 실패와 같이 다루면 <b>"처리하지 못했습니다"</b> 가 뜹니다.
    // 2026-09-03 담당자가 사진 업로드에서 그것을 봤는데, 실제로는 API 가
    // 꺼져 있었습니다 — 다시 눌러 봐야 소용없고, 사람이 할 일도 다릅니다.
    if (status === 502 || status === 503 || status === 504) {
      return Promise.reject(new ApiError({ kind: 'offline' }))
    }

    return Promise.reject(new ApiError({
      kind: 'server',
      message: serverMessage ?? '처리하지 못했습니다. 잠시 뒤 다시 눌러 주십시오.',
    }))
  },
)

/** 사람이 읽을 말로 바꿔 줍니다. 화면마다 같은 문구가 나오게 합니다. */
export function messageOf(e: unknown, fallback: string): string {
  if (!(e instanceof ApiError)) return fallback
  switch (e.failure.kind) {
    case 'offline': return '서버에 닿지 않습니다. 인터넷을 확인하고 잠시 뒤 다시 눌러 주세요.'
    case 'unauthenticated': return '확인 시간이 지났습니다. 예약번호로 다시 들어와 주세요.'
    case 'notFound': return '그런 예약을 찾지 못했습니다.'
    default: return e.message || fallback
  }
}
